import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { humanizeAction, timeAgo } from "../../lib/format.js";
import { AdminTabs } from "../../components/AdminTabs.js";
import { PageSpinner } from "../../components/ui.js";

interface AuditEntry {
  id: string;
  createdAt: string;
  actorUsername: string | null;
  action: string;
  success: boolean;
  ip: string | null;
}

const PAGE = 50;

export function AdminAuditPage() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function load(nextOffset: number) {
    try {
      const data = await api.get<{ entries: AuditEntry[]; total: number }>(
        `/api/admin/audit?limit=${PAGE}&offset=${nextOffset}`
      );
      setEntries(data.entries);
      setTotal(data.total);
      setOffset(nextOffset);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the audit log.");
    }
  }

  useEffect(() => {
    void load(0);
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1>Administration</h1>
        <p className="subtitle">Security &amp; audit log — append-only.</p>
      </div>
      <AdminTabs />

      {error && <div className="alert alert-danger">{error}</div>}
      {!entries ? (
        <PageSpinner />
      ) : entries.length === 0 ? (
        <div className="card center muted">No audit entries yet.</div>
      ) : (
        <>
          <div className="card" style={{ padding: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Result</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="dim" style={{ whiteSpace: "nowrap" }}>{timeAgo(e.createdAt)}</td>
                    <td>{humanizeAction(e.action)}</td>
                    <td className="mono text-xs">{e.actorUsername ?? "—"}</td>
                    <td>
                      <span className={e.success ? "badge badge-success" : "badge badge-danger"}>
                        {e.success ? "ok" : "failed"}
                      </span>
                    </td>
                    <td className="mono text-xs dim">{e.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row-between mt-4">
            <span className="dim text-sm">
              {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
            </span>
            <div className="row">
              <button
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => load(Math.max(0, offset - PAGE))}
              >
                Previous
              </button>
              <button
                className="btn btn-sm"
                disabled={offset + PAGE >= total}
                onClick={() => load(offset + PAGE)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
